/**
 * 배포 화면 상단 — 서비스별로 지금 실제로 떠 있는 이미지 버전.
 *
 *  - 사내 테스트 서버: GET /v0.1/runtime/service-versions (manage-backend 호스트의 docker).
 *    한 번에 한 현장만 떠 있다 — 다른 현장이 떠 있어도 열은 보이고, 그 현장 이름을 적고
 *    비교만 하지 않는다(열을 숨기면 사내 버전을 볼 데가 없다).
 *  - 현장 통합서버: GET /v0.1/site-health/live 의 versions (10분 주기 SSH 점검).
 *    방재실이 여러 곳이면 방재실마다 열 하나. 현장 배포 직후엔 "현장 다시 읽기" 로
 *    점검을 바로 한 바퀴 돌린다(POST /v0.1/site-health/live/refresh).
 *
 * 사내와 현장이 다른 빌드인 칸만 강조한다 — "현장에 아직 안 올라간 것" 을 바로 찾게.
 * 지금 고른 배포 대상의 열은 머리에 "배포 대상" 을 단다.
 *
 * 현장 열의 버전 아래에 그 방재실의 설정 파일(docker/deploy/sites/<코드>/) 칩을 둔다 — 누르면
 * 바로 편집한다. 방재실마다 설정 코드가 달라 열 안에 두어야 어느 방재실 파일인지 바로 보인다.
 * 점검 결과가 없는 설정 폴더(통합서버 미등록·점검 꺼짐)도 열은 만들어 칩을 보인다.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Snackbar, Stack, Tooltip, Typography, alpha } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import type { ServiceVersion } from '../../../models/ServiceVersion';
import type { SiteConfigSite } from '../../../services/SiteConfigService';
import type { DeployTabType } from '../../../views/SiteDashboardView/types';
import { RuntimeService } from '../../../services/RuntimeService';
import { SiteConfigService } from '../../../services/SiteConfigService';
import { configSiteForGroup } from './siteConfigFiles';
import { SiteHealthService } from '../../../services/SiteHealthService';
import { getUserInfo } from '../../../utils/auth';
import { useSiteLiveStatus } from '../../../views/SiteDashboardView/widgets/ServerStatusBadge';
import { useDashboardTokens } from '../../../views/SiteDashboardView/tokens';
import SiteConfigEditorDialog, { type SiteConfigEditorTarget } from './SiteConfigEditorDialog';
import { ConfigChips, ConfigFooter } from './ServiceConfigCells';
import {
  buildDateLabel,
  buildVersionRows,
  compareBuild,
  recheckDone,
  sourceHint,
  type HintTone,
} from './runningVersions';

interface Props {
  projectId: number;
  /** projects.name — 배포 site 이름과 같고, 사내 서버에 떠 있는 현장과 비교한다. */
  projectName: string;
  /** 지금 고른 배포 대상 — 그 열을 강조한다. */
  target: DeployTabType;
}

interface Column {
  key: string;
  title: string;
  /** 열 제목 아래 한 줄 (확인 시각·사유) */
  note: string;
  versions: ServiceVersion[] | null;
  /** 현장 열만 true — 사내 열과 비교해 차이를 표시한다. */
  field: boolean;
  /** 현장 열의 설정 폴더(방재실 이름으로 맞춤) — 버전 아래 설정 파일 칩. */
  config?: SiteConfigSite;
}

const POLL_MS = 60_000;
/**
 * 다시 점검 요청 후 이 간격으로 읽어, 이 현장 확인 시각이 바뀌면 멈춘다. 현장 한 곳 점검은
 * 영상처리서버를 한 대씩 보느라(무응답이면 대당 20초) 1~2분 걸릴 수 있어 고정 대기로는 옛 값을 읽었다.
 */
const RECHECK_POLL_MS = 5_000;
const RECHECK_GIVE_UP_MS = 5 * 60_000;

function clock(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const RunningVersionsPanel: React.FC<Props> = ({ projectId, projectName, target }) => {
  const { colors, type, space } = useDashboardTokens();
  const live = useSiteLiveStatus();
  const company = useQuery({
    queryKey: ['runtime', 'service-versions'],
    queryFn: ({ signal }) => RuntimeService.getServiceVersions(signal),
    staleTime: POLL_MS,
    refetchInterval: POLL_MS,
    retry: false,
  });
  const config = useQuery({
    queryKey: ['site-config', projectId],
    queryFn: () => SiteConfigService.list(projectId),
    staleTime: 60_000,
  });
  const configSites = useMemo(() => config.data?.sites ?? [], [config.data]);
  const role = getUserInfo()?.role;
  const writable = role === 'admin' || role === 'editor';
  const [editing, setEditing] = useState<SiteConfigEditorTarget | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // 현장 다시 읽기: 점검 한 바퀴를 요청하고, 끝날 즈음 live 를 다시 읽는다.
  const [recheck, setRecheck] = useState<'idle' | 'running' | 'failed'>('idle');
  const recheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (recheckTimer.current) clearTimeout(recheckTimer.current);
    },
    [],
  );
  const requestRecheck = async () => {
    setRecheck('running');
    const before = Object.fromEntries(fieldSites.map((s) => [s.code, s.checked_at]));
    const giveUpAt = Date.now() + RECHECK_GIVE_UP_MS;
    try {
      await SiteHealthService.refreshLive();
    } catch {
      setRecheck('failed');
      return;
    }
    const poll = async () => {
      const res = await live.refetch();
      const mine = (res.data?.data ?? []).filter((s) => s.project_id === projectId);
      if (recheckDone(before, mine) || Date.now() > giveUpAt) {
        setRecheck('idle');
        return;
      }
      recheckTimer.current = setTimeout(() => void poll(), RECHECK_POLL_MS);
    };
    recheckTimer.current = setTimeout(() => void poll(), RECHECK_POLL_MS);
  };

  const companyHere = company.data?.site === projectName;
  const fieldSites = useMemo(
    () => (live.data?.data ?? []).filter((s) => s.project_id === projectId),
    [live.data, projectId],
  );

  const columns: Column[] = useMemo(() => {
    const cols: Column[] = [];
    if (company.data) {
      const otherSite = company.data.site && !companyHere ? company.data.site : '';
      cols.push({
        key: 'company',
        title: '사내 테스트 서버',
        note: otherSite
          ? `지금 ${otherSite} 실행 중 · 비교 안 함`
          : company.data.running
            ? '실시간'
            : '서비스 정지됨',
        versions: company.data.versions ?? null,
        field: false,
      });
    }
    const matched = new Set<SiteConfigSite>();
    for (const s of fieldSites) {
      const cfg = configSiteForGroup(configSites, s.group);
      if (cfg) matched.add(cfg);
      cols.push({
        key: `field-${s.code}`,
        title: s.group ? `현장 · ${s.group}` : '현장 통합서버',
        note: s.versions
          ? `${clock(s.checked_at)} 확인`
          : s.server_reason || '버전을 읽지 못함',
        versions: s.versions ?? null,
        field: true,
        config: cfg,
      });
    }
    // 점검 결과가 없는 설정 폴더 — 버전은 없어도 설정 파일은 보여야 한다.
    for (const cfg of configSites) {
      if (matched.has(cfg)) continue;
      cols.push({
        key: `config-${cfg.code}`,
        title: cfg.room ? `현장 · ${cfg.room}` : '현장 통합서버',
        note: '점검 결과 없음',
        versions: null,
        field: true,
        config: cfg,
      });
    }
    return cols;
  }, [companyHere, company.data, fieldSites, configSites]);

  const rows = useMemo(() => buildVersionRows(columns.map((c) => c.versions)), [columns]);
  // 사내에 다른 현장이 떠 있으면 그건 이 현장 빌드가 아니라 비교하지 않는다.
  const companyIdx = companyHere ? columns.findIndex((c) => !c.field) : -1;
  const isTarget = (c: Column) => c.field === (target === 'remote');

  const summary = useMemo(() => {
    let unknown = 0;
    let drift = 0;
    for (const r of rows) {
      for (let i = 0; i < columns.length; i += 1) {
        const v = r.cells[i];
        if (!columns[i].field || !v) continue;
        if (v.source === 'latest') unknown += 1;
        if (companyIdx >= 0 && compareBuild(r.cells[companyIdx], v) === 'different') drift += 1;
      }
    }
    return { unknown, drift };
  }, [rows, columns, companyIdx]);

  const toneColor = (tone: HintTone) =>
    tone === 'danger' ? colors.danger : tone === 'warn' ? colors.warn : colors.muted;

  const emptyNote = live.isLoading || company.isLoading ? '버전을 불러오는 중…' : '';
  // 버전을 하나도 못 읽어도 표는 그린다 — 설정 파일 칸은 버전과 상관없이 쓸 수 있다.
  const versionsNote = columns.length === 0 ? '사내 서버와 현장 통합서버 버전을 모두 읽지 못했습니다.' : '';
  // 현장 열이 없을 때 이유 — 사내 열은 그대로 보여 주고 표 위에 한 줄로 적는다.
  const fieldNote = (() => {
    if (fieldSites.length > 0 || live.isLoading) return '';
    if (live.data && !live.data.enabled) return '이 서버에서 현장 점검이 꺼져 있어 현장 버전을 읽지 못합니다.';
    return '현장 통합서버가 등록되지 않아 현장 버전은 안 보입니다. 현장 설정에서 통합서버를 등록하세요.';
  })();

  const gridCols = {
    xs: '1fr',
    md: `minmax(140px, 0.7fr) ${columns.map((c) => (c.config ? 'minmax(0, 1.3fr)' : 'minmax(0, 1fr)')).join(' ')}`,
  };

  return (
    <Box
      component="section"
      aria-labelledby="running-versions-heading"
      sx={{
        bgcolor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
      data-testid="running-versions-panel"
    >
      {/* 머리: 제목 + 요약 */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
        spacing={1.5}
        sx={{ px: `${space.md}px`, pt: `${space.md}px`, pb: `${space.sm}px` }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: type.caption.size,
              fontWeight: type.caption.weight,
              letterSpacing: type.caption.spacing,
              textTransform: 'uppercase',
              color: colors.muted,
            }}
          >
            Versions &amp; config
          </Typography>
          <Typography
            id="running-versions-heading"
            component="h2"
            sx={{ fontSize: type.h2.size, fontWeight: type.h2.weight, letterSpacing: type.h2.spacing, color: colors.text, lineHeight: 1.25 }}
          >
            서비스별 버전 · 설정
          </Typography>
        </Box>
        {columns.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <SummaryPill
              label="사내와 다름"
              value={companyIdx >= 0 ? summary.drift : '—'}
              color={summary.drift > 0 ? colors.accent : colors.muted}
              title={companyIdx >= 0 ? '현장에 아직 안 올라간 서비스 수' : '사내 서버에 이 현장이 떠 있지 않아 비교하지 않음'}
            />
            <SummaryPill
              label="버전 미상"
              value={summary.unknown}
              color={summary.unknown > 0 ? colors.warn : colors.muted}
              title="latest 태그만 있어 버전을 모르는 서비스 수 — 다음 빌드부터 라벨로 표시됩니다"
            />
            {live.data?.enabled && (
              <Button
                size="small"
                onClick={requestRecheck}
                disabled={recheck === 'running'}
                sx={{ minHeight: 44, textTransform: 'none' }}
                data-testid="running-versions-recheck"
              >
                {recheck === 'running'
                  ? '현장 점검 중…'
                  : recheck === 'failed'
                    ? '다시 읽기 실패 — 재시도'
                    : '현장 다시 읽기'}
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      {(versionsNote || fieldNote) && !emptyNote && (
        <Typography sx={{ px: `${space.md}px`, pb: `${space.sm}px`, color: colors.muted, fontSize: '0.8rem' }} data-testid="running-versions-field-note">
          {versionsNote || fieldNote}
        </Typography>
      )}
      {emptyNote ? (
        <Typography sx={{ px: `${space.md}px`, pb: `${space.md}px`, color: colors.muted, fontSize: type.body.size }}>
          {emptyNote}
        </Typography>
      ) : (
        <Box role="table" aria-label="서비스별 실행 버전">
          {/* 열 제목 (md 이상) */}
          <Box
            role="row"
            sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: gridCols.md,
              borderTop: `1px solid ${colors.border}`,
              bgcolor: alpha(colors.text, 0.025),
            }}
          >
            <HeaderCell title="서비스" note="배포 순서" />
            {columns.map((c) => (
              <HeaderCell
                key={c.key}
                title={c.title}
                note={c.config ? `${c.config.code}/ ${writable ? '눌러서 수정' : '수정은 관리자·편집자'} · ${c.note}` : c.note}
                accent={!c.field}
                target={isTarget(c)}
              />
            ))}
          </Box>

          {rows.map((r, ri) => (
            <Box
              key={r.app}
              role="row"
              sx={{
                display: 'grid',
                gridTemplateColumns: gridCols,
                borderTop: `1px solid ${colors.border}`,
                transition: 'background-color 150ms',
                '&:hover': { bgcolor: alpha(colors.accent, 0.03) },
              }}
              data-testid={`running-version-row-${r.app}`}
            >
              <Box role="rowheader" sx={{ px: `${space.md}px`, py: `${space.sm}px`, display: 'flex', alignItems: 'baseline', gap: 1.25 }}>
                <Typography component="span" sx={{ fontFamily: type.mono.family, fontSize: '0.75rem', color: colors.muted }}>
                  {String(ri + 1).padStart(2, '0')}
                </Typography>
                <Typography component="span" sx={{ fontWeight: 600, color: colors.text, fontSize: '0.9rem' }}>
                  {r.label}
                </Typography>
              </Box>
              {columns.map((c, ci) => (
                <VersionCell
                  key={c.key}
                  columnTitle={isTarget(c) ? `${c.title} · 배포 대상` : c.title}
                  target={isTarget(c)}
                  v={r.cells[ci]}
                  columnKnown={c.versions !== null}
                  drift={c.field && companyIdx >= 0 && compareBuild(r.cells[companyIdx], r.cells[ci]) === 'different'}
                  toneColor={toneColor}
                >
                  {c.field && (
                    <ConfigChips
                      app={r.app}
                      site={c.config}
                      liveSites={fieldSites}
                      loading={config.isLoading}
                      writable={writable}
                      onOpen={setEditing}
                    />
                  )}
                </VersionCell>
              ))}
            </Box>
          ))}
        </Box>
      )}

      <ConfigFooter
        data={config.data}
        error={config.error}
        writable={writable}
        onOpen={setEditing}
        onRefresh={() => void config.refetch()}
      />
      <SiteConfigEditorDialog
        projectId={projectId}
        branch={config.data?.branch ?? ''}
        target={editing}
        onClose={() => setEditing(null)}
        onSaved={(t) => {
          setEditing(null);
          setNotice(`${t.code}/${t.name} PR 로 머지했습니다 — 현장에는 다음 배포 때 반영됩니다`);
          void config.refetch();
        }}
      />
      <Snackbar
        open={notice !== null}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        message={notice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

const SummaryPill: React.FC<{ label: string; value: number | string; color: string; title: string }> = ({
  label,
  value,
  color,
  title,
}) => (
  <Tooltip title={title} arrow>
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        border: `1px solid ${alpha(color, 0.35)}`,
        bgcolor: alpha(color, 0.08),
      }}
    >
      <Typography component="span" sx={{ fontSize: '0.75rem', color }}>
        {label}
      </Typography>
      <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  </Tooltip>
);

const HeaderCell: React.FC<{ title: string; note: string; accent?: boolean; target?: boolean }> = ({
  title,
  note,
  accent,
  target,
}) => {
  const { colors, space } = useDashboardTokens();
  return (
    <Box
      role="columnheader"
      aria-current={target ? 'true' : undefined}
      sx={{
        px: `${space.md}px`,
        py: `${space.sm}px`,
        minWidth: 0,
        bgcolor: target ? alpha(colors.accent, 0.1) : 'transparent',
        boxShadow: target ? `inset 0 -2px 0 ${colors.accent}` : 'none',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: accent ? colors.accent : colors.text }} noWrap>
          {title}
        </Typography>
        {target && (
          <Typography
            component="span"
            sx={{ flexShrink: 0, px: 0.75, borderRadius: 1, bgcolor: colors.accent, color: '#fff', fontSize: '0.66rem', fontWeight: 700 }}
          >
            배포 대상
          </Typography>
        )}
      </Stack>
      <Typography sx={{ fontSize: '0.72rem', color: colors.muted }} noWrap title={note}>
        {note}
      </Typography>
    </Box>
  );
};

const VersionCell: React.FC<{
  columnTitle: string;
  target: boolean;
  v: ServiceVersion | null;
  columnKnown: boolean;
  drift: boolean;
  toneColor: (t: HintTone) => string;
  /** 버전 아래 — 현장 열의 설정 파일 칩 */
  children?: React.ReactNode;
}> = ({ columnTitle, target, v, columnKnown, drift, toneColor, children }) => {
  const { colors, type, space } = useDashboardTokens();
  const hint = v ? sourceHint(v) : null;
  const date = v ? buildDateLabel(v.created) : '';
  const detail = v
    ? [
        v.image && `이미지 ${v.image}`,
        v.revision && `커밋 ${v.revision.slice(0, 9)}`,
        v.tags?.length ? `태그 ${v.tags.join(', ')}` : '',
        v.created && `빌드 ${new Date(v.created).toLocaleString('ko-KR')}`,
      ]
        .filter(Boolean)
        .join('\n')
    : '';

  return (
    <Box
      role="cell"
      sx={{
        position: 'relative',
        px: `${space.md}px`,
        py: `${space.sm}px`,
        minWidth: 0,
        borderLeft: { xs: 'none', md: `1px solid ${colors.border}` },
        bgcolor: drift ? alpha(colors.accent, 0.06) : 'transparent',
        '&::before': drift
          ? { content: '""', position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 2, bgcolor: colors.accent }
          : undefined,
      }}
    >
      {/* 좁은 화면에서는 열 제목이 없으므로 칸 안에 적는다 */}
      <Typography
        sx={{ display: { xs: 'block', md: 'none' }, fontSize: '0.7rem', color: target ? colors.accent : colors.muted, fontWeight: target ? 700 : 400 }}
      >
        {columnTitle}
      </Typography>
      {!columnKnown || !v ? (
        <Typography sx={{ color: colors.muted, fontSize: '0.85rem' }}>{columnKnown ? '—' : '확인 못함'}</Typography>
      ) : (
        <Tooltip title={detail ? <Box sx={{ whiteSpace: 'pre-line' }}>{detail}</Box> : ''} arrow placement="top-start">
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: type.mono.family,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  color: v.state === 'missing' ? colors.muted : colors.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {v.version || '—'}
              </Typography>
              {drift && (
                <Typography
                  component="span"
                  sx={{ flexShrink: 0, fontSize: '0.68rem', fontWeight: 700, color: colors.accent, letterSpacing: '0.02em' }}
                >
                  사내와 다름
                </Typography>
              )}
            </Stack>
            <Typography sx={{ fontSize: '0.72rem', color: toneColor(hint!.tone) }} noWrap>
              {[date, hint!.text].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Tooltip>
      )}
      {children}
    </Box>
  );
};

export default RunningVersionsPanel;
